import { Transforms, Editor, Element, Node, Text } from 'slate';

const withNormalization = editor => {
	const { normalizeNode } = editor;

	// Normalize each node
	editor.normalizeNode = entry => {
		const [node, path] = entry;

		// Heading normalization
		if (Element.isElement(node) && ['heading-one', 'heading-two', 'heading-three'].includes(node.type)) {
			for (const [child, childPath] of Node.children(editor, path)) {
				// Unwrap link node in heading
				if (Element.isElement(child) && child.type === 'link') {
					Transforms.unwrapNodes(editor, {
						at: childPath,
						match: node => node.type === 'link'
					});
					return;
				}

				// Remove every other element node in heading
				if (Element.isElement(child)) {
					Transforms.removeNodes(editor, {
						at: childPath
					});
					return;
				}
			}
		}

		// Paragraph normalization
		if (Element.isElement(node) && node.type === 'paragraph') {
			for (const [child, childPath] of Node.children(editor, path)) {
				// Unwrap block nodes in paragraph
				if (Element.isElement(child) && !editor.isInline(child)) {
					Transforms.unwrapNodes(editor, {
						at: childPath
					});
					return;
				}
			}
		}

		// List normalization
		if (Element.isElement(node) && ['bulleted-list', 'numbered-list'].includes(node.type)) {
			for (const [child, childPath] of Node.children(editor, path)) {
				if (Element.isElement(child) && child.type !== 'list-item') {
					Transforms.setNodes(editor, {
						type: 'list-item'
					}, {
						at: childPath
					});
					return;
				}
			}
		}

		// List item normalization
		if (Element.isElement(node) && node.type === 'list-item') {
			const parent = Node.parent(editor, path);
			if (!Element.isElement(parent) && !['bulleted-list', 'numbered-list'].includes(parent.type)) {
				Transforms.setNodes(editor, {
					type: 'paragraph'
				}, {
					at: path
				});
				return;
			}
		}

		// Table normalization
		if (Element.isElement(node) && node.type === 'table') {
			for (const [child, childPath] of Node.children(editor, path)) {
				// Allow only table-row elements
				if (Element.isElement(child) && child.type !== 'table-row') {
					Transforms.removeNodes(editor, {
						at: childPath,
						match: n => n.type === child.type,
					});
					return;
				}

				// Remove any other element
				if (!child.type) {
					Transforms.removeNodes(editor, {
						at: childPath,
						match: n => n.type === node.type
					});
					return;
				}
			}
		}

		// Table row normalization
		if (Element.isElement(node) && node.type === 'table-row') {
			for (const [child, childPath] of Node.children(editor, path)) {
				// Allow only table-cell elements
				if (Element.isElement(child) && child.type !== 'table-cell') {
					Transforms.setNodes(editor, {
						type: 'table-cell'
					}, {
						at: childPath
					});
					return;
				}

				if (!child.type) {
					Transforms.removeNodes(editor, {
						at: childPath
					});
					return;
				}
			}
		}

		// Table cell normalization
		if (Element.isElement(node) && node.type === 'table-cell') {
			for (const [child, childPath] of Node.children(editor, path)) {
				// Unwrap block nodes in table cell
				if (Element.isElement(child) && !editor.isInline(child)) {
					Transforms.unwrapNodes(editor, {
						at: childPath
					});
					return;
				}
			}
		}
		
		// Blockquote normalization
		if (Element.isElement(node) && node.type === 'blockquote') {
			for (const [child, childPath] of Node.children(editor, path)) {
				// Unwrap block nodes in blockquotes
				if (Element.isElement(child) && !editor.isInline(child)) {
					Transforms.unwrapNodes(editor, {
						at: childPath
					});
					return;
				}
			}
		}

		// Quiz normalization
		if (Element.isElement(node) && node.type === 'quiz') {
			for (const [child, childPath] of Node.children(editor, path)) {
				// Allow only quiz-question elements
				if (Element.isElement(child) && child.type !== 'quiz-question') {
					Transforms.removeNodes(editor, {
						at: childPath,
						match: n => n.type === child.type,
					});
					return;
				}

				// Remove any other element
				if (!child.type) {
					Transforms.removeNodes(editor, {
						at: childPath,
						match: n => n.type === node.type
					});
					return;
				}
			}
		}

		// Quiz question normalization
		if (Element.isElement(node) && node.type === 'quiz-question') {
			for (const [child, childPath] of Node.children(editor, path)) {
				// Allow only quiz-content and quiz-option elements
				if (Element.isElement(child) && !['quiz-content', 'quiz-option'].includes(child.type)) {
					Transforms.removeNodes(editor, {
						at: childPath,
						match: n => n.type === child.type,
					});
					return;
				}

				// Remove any other element
				if (!child.type) {
					Transforms.removeNodes(editor, {
						at: childPath,
						match: n => n.type === node.type
					});
					return;
				}
			}
		}

		// Quiz content normalization
		if (Element.isElement(node) && node.type === 'quiz-content') {
			for (const [child, childPath] of Node.children(editor, path)) {
				// Unwrap block nodes in quiz content
				if (Element.isElement(child) && !editor.isInline(child)) {
					Transforms.unwrapNodes(editor, {
						at: childPath
					});
					return;
				}
			}
		}

		// Quiz option normalization
		if (Element.isElement(node) && node.type === 'quiz-option') {
			for (const [child, childPath] of Node.children(editor, path)) {
				// Unwrap block nodes in quiz option
				if (Element.isElement(child) && !editor.isInline(child)) {
					Transforms.unwrapNodes(editor, {
						at: childPath
					});
					return;
				}
			}
		}

		// Embed normalization
		if (Element.isElement(node) && ['image', 'video', 'audio'].includes(node.type)) {
			// Get next element
			const next = Editor.next(editor, {
				at: path
			});

			// Get previous element
			const prev = Editor.previous(editor, {
				at: path
			});

			if (!next) {
				Transforms.insertNodes(editor, {
					type: 'paragraph',
					children: [{ text: '' }]
				}, {
					at: [path[0] + 1]
				});
				return;
			}

			if (!prev) {
				Transforms.insertNodes(editor, {
					type: 'paragraph',
					children: [{ text: '' }]
				}, {
					at: [0]
				});
				return;
			}
		}

		if (Element.isElement(node)) {
			// Get last element in editor
			const last = Node.get(editor, [editor?.children?.length - 1]);

			// Dissalowed last elements
			const disallowed = ['quiz', 'image', 'audio', 'youtube', 'download', 'table'];

			// Insert paragraph if last element is not allowed
			if (disallowed.includes(last?.type)) {
				Transforms.insertNodes(editor, {
					type: 'paragraph',
					children: [{ text: '' }]
				}, {
					at: [editor?.children?.length]
				});
			}
		}

		normalizeNode(entry);
	};

	return editor;
};

export default withNormalization;